import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../../../components/layout/Navbar";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { ArrowLeft, User, Mail, Lock, Phone, Building2, DollarSign, Target, Users, FileText, Calendar } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { useAuth } from "../../../contexts/AuthContext";
import { useLanguage } from "../../../contexts/LanguageContext";

const AdminCreateAccount = () => {
  const [accountType, setAccountType] = useState<"admin" | "investor" | "project-holder">("investor");
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    // Common fields
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    company: "",
    linkedin: "",

    // Investor fields
    investmentCapacity: "",
    preferredSectors: "",

    investmentExperience: "",
    occupation: "",

    // Project holder fields
    projectName: "",
    projectDescription: "",
    sector: "",
    teamSize: "",
    projectPurpose: "getInvestment", // Default to investment
    location: "", // Add location field
    assetsIncluded: [] // Assets included as checkmarks
  });

  const { registerAdmin, registerInvestor, registerProjectHolder } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return;
    }

    if (!formData.name || !formData.email || !formData.password) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      let success = false;

      if (accountType === "admin") {
        success = await registerAdmin({
          name: formData.name,
          email: formData.email,
          password: formData.password
        });
      } else if (accountType === "investor") {
        success = await registerInvestor({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone || undefined,
          company: formData.company || undefined,
          investmentCapacity: formData.investmentCapacity ? parseFloat(formData.investmentCapacity) : undefined,
          preferredSectors: formData.preferredSectors ? formData.preferredSectors.split(',').map(s => s.trim()) : undefined,

          investmentExperience: formData.investmentExperience || undefined,
          occupation: formData.occupation || undefined,
          linkedin: formData.linkedin || undefined,
        });
      } else if (accountType === "project-holder") {
        success = await registerProjectHolder({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone || undefined,
          company: formData.company || undefined,
          bio: formData.projectDescription || undefined,
          projectName: formData.projectName || undefined,
          projectDescription: formData.projectDescription || undefined,
          sector: formData.sector || undefined,
          teamSize: formData.teamSize ? parseInt(formData.teamSize) : undefined,
          linkedin: formData.linkedin || undefined,
          projectFiles: [],
          projectUrls: [],
          website: "",
          projectPurpose: formData.projectPurpose as "sell" | "getInvestment" || "getInvestment", // Use the selected project purpose
          location: formData.projectPurpose === 'sell' ? formData.location || undefined : undefined, // Add location only if project purpose is 'sell'
          assetsIncluded: formData.projectPurpose === 'sell' ? formData.assetsIncluded || [] : [] // Assets included only if project purpose is 'sell'
        });
      }

      if (success) {
        toast({
          title: "Account Created",
          description: `${accountType.charAt(0).toUpperCase() + accountType.slice(1)} account created successfully`,
        });
        navigate("/admin");
      } else {
        throw new Error("Failed to create account");
      }
    } catch (error) {
      toast({
        title: "Creation Failed",
        description: error instanceof Error ? error.message : "Failed to create account",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />

      <main className="flex-1 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {language === "ar" ? "العودة للوحة الإدارة" : "Back to Admin Dashboard"}
          </Link>

          <Card className="border-border shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                {language === "ar" ? "إنشاء حساب جديد" : "Create New Account"}
              </CardTitle>
              <CardDescription>
                {language === "ar" ? "املأ النموذج أدناه لإنشاء حساب جديد" : "Fill out the form below to create a new account"}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Account Type Selection */}
                <div className="space-y-2">
                  <Label>{language === "ar" ? "نوع الحساب" : "Account Type"}</Label>
                  <Select
                    value={accountType}
                    onValueChange={(value: "admin" | "investor" | "project-holder") => setAccountType(value)}
                    required
                  >
                    <SelectTrigger className={language === "ar" ? "pr-10" : "pl-10"}>
                      <SelectValue placeholder={language === "ar" ? "اختر نوع الحساب" : "Select account type"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">{language === "ar" ? "مشرف" : "Admin"}</SelectItem>
                      <SelectItem value="investor">{language === "ar" ? "مستثمر" : "Investor"}</SelectItem>
                      <SelectItem value="project-holder">{language === "ar" ? "صاحب مشروع" : "Project Holder"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Common Information Section */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    {language === "ar" ? "المعلومات الأساسية" : "Basic Information"}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">{language === "ar" ? "الاسم الكامل" : "Full Name"}</Label>
                      <div className="relative">
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          placeholder={language === "ar" ? "أدخل اسمك الكامل" : "Enter your full name"}
                          value={formData.name}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <User className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">{language === "ar" ? "البريد الإلكتروني" : "Email"}</Label>
                      <div className="relative">
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="example@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Mail className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">{language === "ar" ? "رقم الهاتف" : "Phone"}</Label>
                      <div className="relative">
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder={language === "ar" ? "+213 5XX XXX XXXX" : "+1 (555) 123-4567"}
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                        />
                        <Phone className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">{language === "ar" ? "اسم الشركة" : "Company Name"}</Label>
                      <div className="relative">
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          placeholder={language === "ar" ? "أدخل اسم شركتك" : "Enter your company name"}
                          value={formData.company}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                        />
                        <Building2 className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Security Section */}
                <div className="border-b pb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    {language === "ar" ? "أمان الحساب" : "Account Security"}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="password">{language === "ar" ? "كلمة المرور" : "Password"}</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          name="password"
                          type="password"
                          placeholder={language === "ar" ? "أنشئ كلمة مرور قوية" : "Create a strong password"}
                          value={formData.password}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Lock className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">{language === "ar" ? "تأكيد كلمة المرور" : "Confirm Password"}</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          name="confirmPassword"
                          type="password"
                          placeholder={language === "ar" ? "أكد كلمة المرور الخاصة بك" : "Confirm your password"}
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                          className={language === "ar" ? "pr-10" : "pl-10"}
                          required
                        />
                        <Lock className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Investor Specific Fields */}
                {accountType === "investor" && (
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      {language === "ar" ? "معلومات المستثمر" : "Investor Information"}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="investmentCapacity">{language === "ar" ? "سعة الاستثمار" : "Investment Capacity"}</Label>
                        <div className="relative">
                          <Input
                            id="investmentCapacity"
                            name="investmentCapacity"
                            type="number"
                            placeholder={language === "ar" ? "أدخل سعة الاستثمار" : "Enter investment capacity"}
                            value={formData.investmentCapacity}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <DollarSign className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>



                      <div className="space-y-2">
                        <Label htmlFor="preferredSectors">{language === "ar" ? "القطاعات المفضلة" : "Preferred Sectors"}</Label>
                        <div className="relative">
                          <Input
                            id="preferredSectors"
                            name="preferredSectors"
                            type="text"
                            placeholder={language === "ar" ? "القطاعات مفصولة بفواصل" : "Sectors separated by commas"}
                            value={formData.preferredSectors}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <Building2 className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="occupation">{language === "ar" ? "المهنة" : "Occupation"}</Label>
                        <div className="relative">
                          <Input
                            id="occupation"
                            name="occupation"
                            type="text"
                            placeholder={language === "ar" ? "أدخل مهنتك" : "Enter your occupation"}
                            value={formData.occupation}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <User className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="investmentExperience">{language === "ar" ? "تجربة الاستثمار" : "Investment Experience"}</Label>
                        <div className="relative">
                          <Input
                            id="investmentExperience"
                            name="investmentExperience"
                            type="text"
                            placeholder={language === "ar" ? "أدخل تجربة الاستثمار" : "Enter investment experience"}
                            value={formData.investmentExperience}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <FileText className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="linkedin">{language === "ar" ? "LinkedIn" : "LinkedIn"}</Label>
                        <div className="relative">
                          <Input
                            id="linkedin-investor"
                            name="linkedin"
                            type="url"
                            placeholder="https://linkedin.com/in/yourprofile"
                            value={formData.linkedin}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <FileText className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Project Holder Specific Fields */}
                {accountType === "project-holder" && (
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      {language === "ar" ? "معلومات المشروع" : "Project Information"}
                    </h3>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="projectName">{language === "ar" ? "اسم المشروع" : "Project Name"}</Label>
                        <div className="relative">
                          <Input
                            id="projectName"
                            name="projectName"
                            type="text"
                            placeholder={language === "ar" ? "أدخل اسم مشروعك" : "Enter your project name"}
                            value={formData.projectName}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <FileText className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="projectDescription">{language === "ar" ? "وصف المشروع" : "Project Description"}</Label>
                        <Textarea
                          id="projectDescription"
                          name="projectDescription"
                          placeholder={language === "ar" ? "صف مشروعك بالتفصيل. تضمين المشكلة التي يحلها، السوق المستهدف، الميزة التنافسية، والنماذج التجارية." : "Describe your project in detail. Include the problem it solves, target market, competitive advantage, and business model."}
                          value={formData.projectDescription}
                          onChange={handleInputChange}
                          className="min-h-[120px]"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="sector">{language === "ar" ? "القطاع" : "Sector"}</Label>
                          <div className="relative">
                            <Input
                              id="sector"
                              name="sector"
                              type="text"
                              placeholder={language === "ar" ? "أدخل القطاع" : "Enter sector"}
                              value={formData.sector}
                              onChange={handleInputChange}
                              className={language === "ar" ? "pr-10" : "pl-10"}
                            />
                            <Building2 className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="teamSize">{language === "ar" ? "حجم الفريق" : "Team Size"}</Label>
                          <div className="relative">
                            <Input
                              id="teamSize"
                              name="teamSize"
                              type="number"
                              placeholder={language === "ar" ? "عدد أعضاء الفريق" : "Number of team members"}
                              value={formData.teamSize}
                              onChange={handleInputChange}
                              className={language === "ar" ? "pr-10" : "pl-10"}
                            />
                            <Users className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="projectPurpose">{language === "ar" ? t("projectPurpose.title") : t("projectPurpose.title")}</Label>
                        <Select
                          value={formData.projectPurpose}
                          onValueChange={(value) => handleSelectChange("projectPurpose", value)}
                          required
                        >
                          <SelectTrigger id="projectPurpose" className={language === "ar" ? "pr-10" : "pl-10"}>
                            <SelectValue placeholder={language === "ar" ? t("projectPurpose.selectPurpose") : t("projectPurpose.selectPurpose")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sell">{language === "ar" ? t("projectPurpose.wantToSell") : t("projectPurpose.wantToSell")}</SelectItem>
                            <SelectItem value="getInvestment">{language === "ar" ? t("projectPurpose.wantToGetInvestment") : t("projectPurpose.wantToGetInvestment")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="linkedin-project-holder">{language === "ar" ? "LinkedIn" : "LinkedIn"}</Label>
                        <div className="relative">
                          <Input
                            id="linkedin-project-holder"
                            name="linkedin"
                            type="url"
                            placeholder="https://linkedin.com/in/yourprofile"
                            value={formData.linkedin}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <FileText className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Project Details Section - Only for project holders with purpose "sell" */}
                {accountType === "project-holder" && formData.projectPurpose === "sell" && (
                  <div className="border-b pb-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      {language === "ar" ? "تفاصيل المشروع" : "Project Details"}
                    </h3>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="location">{language === "ar" ? "الموقع" : "Location"}</Label>
                        <div className="relative">
                          <Input
                            id="location"
                            name="location"
                            type="text"
                            placeholder={language === "ar" ? "أدخل موقع المشروع" : "Enter project location"}
                            value={formData.location}
                            onChange={handleInputChange}
                            className={language === "ar" ? "pr-10" : "pl-10"}
                          />
                          <Target className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>{language === "ar" ? "الأصول المدرجة" : "Assets Included"}</Label>
                        <div className="space-y-2">
                          {[
                            { id: "land", label: language === "ar" ? "أرض" : "Land" },
                            { id: "buildings", label: language === "ar" ? "مباني" : "Buildings" },
                            { id: "equipment", label: language === "ar" ? "معدات" : "Equipment" },
                            { id: "intellectualProperty", label: language === "ar" ? "ملكية فكرية" : "Intellectual Property" },
                            { id: "inventory", label: language === "ar" ? "جرد" : "Inventory" },
                            { id: "other", label: language === "ar" ? "أخرى" : "Other" },
                          ].map((asset) => (
                            <div key={asset.id} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`asset-${asset.id}`}
                                checked={formData.assetsIncluded.includes(asset.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData(prev => ({
                                      ...prev,
                                      assetsIncluded: [...prev.assetsIncluded, asset.id]
                                    }));
                                  } else {
                                    setFormData(prev => ({
                                      ...prev,
                                      assetsIncluded: prev.assetsIncluded.filter(id => id !== asset.id)
                                    }));
                                  }
                                }}
                                className="h-4 w-4"
                              />
                              <Label htmlFor={`asset-${asset.id}`} className="text-sm font-normal cursor-pointer">
                                {asset.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>


                    </div>
                  </div>
                )}

                {/* Additional Information Section */}
                <div className="pb-6">
                  <h3 className="text-lg font-semibold mb-4">{language === "ar" ? "معلومات إضافية" : "Additional Information"}</h3>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="linkedin-additional">{language === "ar" ? "LinkedIn" : "LinkedIn Profile"}</Label>
                      <Input
                        id="linkedin-additional"
                        name="linkedin"
                        type="url"
                        placeholder="https://linkedin.com/in/yourprofile"
                        value={formData.linkedin}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button
                    type="submit"
                    className="w-full sm:w-auto bg-primary hover:bg-primary/90"
                  >
                    {language === "ar" ? "إنشاء الحساب" : "Create Account"}
                  </Button>

                  <Button type="button" variant="outline" asChild className="w-full sm:w-auto">
                    <Link to="/admin">
                      {language === "ar" ? "إلغاء" : "Cancel"}
                    </Link>
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>


    </div>
  );
};

export default AdminCreateAccount;